 
import React, {forwardRef, useRef} from "react";

interface IDataTableV2Config {
  tableConfig?: {
    stickyHeader?: boolean;
    stickyHeaderTableMaxHeight?: string;
  };
  cachingFilters?: {
    enableCaching: boolean;
    saveCachingIntoServer?: (updatedCachingFilter: any) => void;
    getCachingConfigFromServerAtLoad?: () => any;
    getUrlAndProperties : (data: any) => ({url: string, otherParams: any})
  };
}

interface IDataTableV2Context extends IDataTableV2Config {
  getCachedDataByKey: (uniqueKey: string) => any | null;
  saveDataInCache: (uniqueKey: string, updatedData: any) => void;
  cachingDataState: IDataTableV2CachedDataState;
}

const DataTableContext = React.createContext<IDataTableV2Context>({
  tableConfig: {},
  getCachedDataByKey: () => {},
  saveDataInCache: () => {},
  cachingDataState: {},
});

export interface IDataTableV2CachedDataState {
  [key: string]: any;
}

export interface DataTableV2ProviderHandle {
  getCachingConfigFromServerAtLoad?: () => any;
}

export const useDataTableV2Context = () => React.useContext(DataTableContext);

export const DataTableV2Provider=forwardRef<
DataTableV2ProviderHandle,
{ children?: React.ReactNode } & IDataTableV2Config
>((props, ref) => {
  const {
    cachingFilters = { enableCaching: false, ...props.cachingFilters },
    tableConfig,
  } = props;
  const { enableCaching } = cachingFilters;

  const [cachingDataState, setCachingDataState] =
    React.useState<IDataTableV2CachedDataState>({});
    const isDirty = useRef(false); 

  const getCachedDataByKey = (uniqueKey: string) => {
    if (cachingDataState[uniqueKey]) {
      return cachingDataState[uniqueKey];
    }
    return null;
  };

  const saveDataInCache = (uniqueKey: string, updatedData: any) => {
    if (enableCaching) {
      const cachedData = localStorage.getItem("tablePreferences");
      if (cachedData) {
        const parsedData = JSON.parse(cachedData);
        const finalData = { ...parsedData };
        finalData[uniqueKey] = updatedData;
        setCachingDataState(finalData);
        localStorage.setItem("tablePreferences", JSON.stringify(finalData));
        isDirty.current = true; 
        if (cachingFilters.saveCachingIntoServer) {
          cachingFilters.saveCachingIntoServer(finalData);
        }
      } else {
        const finalData: any = {};
        finalData[uniqueKey] = updatedData;
        setCachingDataState(finalData);
        localStorage.setItem("tablePreferences", JSON.stringify(finalData));
        isDirty.current = true; 
        if (cachingFilters.saveCachingIntoServer) {
          cachingFilters.saveCachingIntoServer(finalData);
        }
      }
    }
  };

  React.useEffect(() => {
    (async () => {
      if (enableCaching) {
          const cachedData = localStorage.getItem("tablePreferences");
          if (cachedData) {
            const parsedData = JSON.parse(cachedData);
            setCachingDataState(parsedData);
          }
        
      }
    })();
  }, []);

  React.useImperativeHandle(ref, () => ({
    getCachingConfigFromServerAtLoad: async()=> {
     if( cachingFilters.getCachingConfigFromServerAtLoad){
      const data = await cachingFilters.getCachingConfigFromServerAtLoad();
      localStorage.setItem("tablePreferences", JSON.stringify(data));
      setCachingDataState(data);
     }
    },
  }));

  React.useEffect(() => {
    const interval = setInterval(() => {
      if (isDirty.current && cachingDataState) {
       if (cachingFilters.saveCachingIntoServer) {
          cachingFilters.saveCachingIntoServer(cachingDataState);
        }
        isDirty.current = false;
      }
    }, 30 * 60 * 1000); // 15 minutes

    return () => clearInterval(interval);
  }, [cachingDataState]);

   React.useEffect(() => {
    const handleBeforeUnload = () => {
      if (cachingDataState) {
          if (cachingFilters.getUrlAndProperties) {
            const {url, otherParams} = cachingFilters.getUrlAndProperties(cachingDataState);
            navigator.sendBeacon(url ,otherParams);
        }
      
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [cachingDataState]);

  return (
    <DataTableContext.Provider
      value={{
        tableConfig: tableConfig,
        getCachedDataByKey: getCachedDataByKey,
        saveDataInCache,
        cachingDataState: cachingDataState,
      }}
    >
      {props.children}
    </DataTableContext.Provider>
  );
});
